import React, { useEffect, useState } from 'react';
import { apiService, RankingTimelineResponse, RankingTimelineSnapshot } from '../services/api';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface RankingSnapshotsTableProps {
  sku: string;
  rangeHours: number;
}

type SortField = 'timestamp' | 'ranking_position' | 'rank_atual' | 'rank_c_stock' | 'rank_s_stock' | 'final_score' | 'penalty_v2_factor';
type SortDirection = 'asc' | 'desc';

const RankingSnapshotsTable: React.FC<RankingSnapshotsTableProps> = ({ sku, rangeHours }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<RankingTimelineResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getProductRankingTimeline(sku, rangeHours);
        setData(response);
      } catch (err: any) {
        console.error('Error fetching ranking timeline:', err);
        setError(err.response?.data?.error || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    if (sku) {
      fetchTimeline();
    }
  }, [sku, rangeHours]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedSnapshots = (): RankingTimelineSnapshot[] => {
    if (!data) return [];

    const sorted = [...data.snapshots].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Convert to numbers for comparison if needed
      if (typeof aVal === 'string' && sortField === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const getPenaltyBadgeClass = (factor: number | null): string => {
    if (factor === null) return 'bg-secondary';
    if (factor >= 0.9) return 'bg-success';
    if (factor >= 0.7) return 'bg-warning';
    if (factor >= 0.5) return 'bg-orange';
    return 'bg-danger';
  };

  const getCategoryBadgeClass = (category: string | null): string => {
    if (!category) return 'bg-secondary';
    switch (category.toLowerCase()) {
      case 'ultra-fast':
        return 'bg-danger';
      case 'fast':
        return 'bg-warning';
      case 'medium':
        return 'bg-info';
      case 'slow':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  const highlightSignificantChange = (
    current: number | null,
    previous: number | null,
    threshold: number
  ): boolean => {
    if (current === null || previous === null) return false;
    return Math.abs(current - previous) >= threshold;
  };

  const navigateToSnapshot = (timestamp: string | null) => {
    if (timestamp) {
      // Navigate to ranking analysis page with this snapshot
      navigate(`/ranking-analysis?snapshot=${encodeURIComponent(timestamp)}`);
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortDirection === 'asc' ? (
      <i className="bi bi-arrow-up text-primary ms-1"></i>
    ) : (
      <i className="bi bi-arrow-down text-primary ms-1"></i>
    );
  };

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="text-muted mt-2 mb-0 small">A carregar tabela de snapshots...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm border-warning">
        <div className="card-body">
          <p className="text-warning mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.snapshots.length === 0) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-4">
          <i className="bi bi-inbox text-muted fs-1"></i>
          <p className="text-muted mt-2 mb-0">Sem snapshots disponíveis para este período</p>
        </div>
      </div>
    );
  }

  const sortedSnapshots = getSortedSnapshots();

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light">
        <h6 className="mb-0">
          <i className="bi bi-table me-2"></i>
          Detalhes dos Snapshots ({data.count} registos)
        </h6>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSort('timestamp')}
                  style={{ cursor: 'pointer' }}
                >
                  Timestamp <SortIcon field="timestamp" />
                </th>
                <th
                  className="text-center cursor-pointer"
                  onClick={() => handleSort('rank_atual')}
                  style={{ cursor: 'pointer' }}
                >
                  Rank Atual <SortIcon field="rank_atual" />
                </th>
                <th
                  className="text-center cursor-pointer"
                  onClick={() => handleSort('rank_c_stock')}
                  style={{ cursor: 'pointer' }}
                >
                  Rank C/ Stock <SortIcon field="rank_c_stock" />
                </th>
                <th
                  className="text-center cursor-pointer"
                  onClick={() => handleSort('rank_s_stock')}
                  style={{ cursor: 'pointer' }}
                >
                  Rank S/ Stock <SortIcon field="rank_s_stock" />
                </th>
                <th
                  className="text-end cursor-pointer"
                  onClick={() => handleSort('final_score')}
                  style={{ cursor: 'pointer' }}
                >
                  Score <SortIcon field="final_score" />
                </th>
                <th
                  className="text-center cursor-pointer"
                  onClick={() => handleSort('penalty_v2_factor')}
                  style={{ cursor: 'pointer' }}
                >
                  Penalty V2 <SortIcon field="penalty_v2_factor" />
                </th>
                <th className="text-center">Categoria</th>
                <th className="text-end">Stock</th>
                <th>Notas</th>
                <th className="text-center">Ação</th>
              </tr>
            </thead>
            <tbody>
              {sortedSnapshots.map((snapshot, index) => {
                const previousSnapshot = sortedSnapshots[index - 1];
                const positionChange = highlightSignificantChange(
                  snapshot.ranking_position,
                  previousSnapshot?.ranking_position,
                  10
                );
                const penaltyChange =
                  snapshot.penalty_v2_factor &&
                  previousSnapshot?.penalty_v2_factor &&
                  snapshot.penalty_v2_factor < 0.7;

                return (
                  <tr key={snapshot.timestamp || index} className={positionChange || penaltyChange ? 'table-warning' : ''}>
                    <td className="small">
                      {snapshot.timestamp
                        ? format(parseISO(snapshot.timestamp), 'dd/MM/yyyy HH:mm')
                        : 'N/A'}
                    </td>
                    <td className="text-center fw-bold">
                      #{snapshot.rank_atual || 'N/A'}
                    </td>
                    <td className="text-center">
                      #{snapshot.rank_c_stock || 'N/A'}
                      {positionChange && (
                        <i className="bi bi-exclamation-circle-fill text-warning ms-1" title="Mudança significativa"></i>
                      )}
                    </td>
                    <td className="text-center">
                      #{snapshot.rank_s_stock || 'N/A'}
                    </td>
                    <td className="text-end">
                      {snapshot.final_score !== null ? snapshot.final_score.toFixed(0) : 'N/A'}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getPenaltyBadgeClass(snapshot.penalty_v2_factor)}`}>
                        {snapshot.penalty_v2_factor !== null
                          ? `${Math.round(snapshot.penalty_v2_factor * 100)}%`
                          : 'N/A'}
                      </span>
                      {penaltyChange && (
                        <i className="bi bi-exclamation-circle-fill text-danger ms-1" title="Penalização severa"></i>
                      )}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getCategoryBadgeClass(snapshot.penalty_v2_category)} text-uppercase`}>
                        {snapshot.penalty_v2_category || 'N/A'}
                      </span>
                    </td>
                    <td className="text-end">{snapshot.stock_quantity !== null ? snapshot.stock_quantity : 'N/A'}</td>
                    <td className="small text-truncate" style={{ maxWidth: '200px' }} title={snapshot.notas || ''}>
                      {snapshot.notas || '-'}
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigateToSnapshot(snapshot.timestamp)}
                        title="Ver snapshot completo"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-footer bg-light text-muted small">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <i className="bi bi-info-circle me-1"></i>
            Linhas destacadas indicam mudanças significativas (posição +/-10 ou penalização &lt; 70%)
          </div>
          <div>
            Total: <strong>{data.count}</strong> snapshots
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingSnapshotsTable;
